/**
 * Los botones de categorías de la Caja: las categorías y, debajo, sus
 * subcategorías.
 *
 * Lo pidió el dueño: «Placas madres» y dentro «AMD». En la Caja se toca
 * «Placas madres» y aparece una segunda fila con «AMD», «Intel»…
 *
 * ⚠️ Se sacan de los PRODUCTOS guardados en el aparato, no de otra consulta:
 * así la fila funciona sin internet, como funcionaba la de siempre. Por eso
 * cada producto trae también su categoría madre (`category_parent_*`).
 *
 * Aparte de `PosPage` para poder probarlo.
 */

import type { ItemListRow } from "@/lib/types";

export type BotonDeCategoria = { id: string; nombre: string; color: string | null };

type ConCategoria = Pick<
  ItemListRow,
  | "category_id"
  | "category_name"
  | "category_color"
  | "category_parent_id"
  | "category_parent_name"
  | "category_parent_color"
>;

const porNombre = (a: BotonDeCategoria, b: BotonDeCategoria) => a.nombre.localeCompare(b.nombre);

/**
 * Las categorías de arriba (de los productos, o las madres de sus
 * subcategorías) y las subcategorías de cada una. Solo las que tienen algo
 * que vender: un botón que no enseña nada es un toque perdido.
 */
export function categoriasDeLaCaja(items: readonly ConCategoria[]): {
  madres: BotonDeCategoria[];
  hijasDe: Map<string, BotonDeCategoria[]>;
} {
  const madres = new Map<string, BotonDeCategoria>();
  const hijas = new Map<string, Map<string, BotonDeCategoria>>();

  for (const i of items) {
    if (!i.category_id || !i.category_name) continue;
    if (i.category_parent_id && i.category_parent_name) {
      if (!madres.has(i.category_parent_id)) {
        madres.set(i.category_parent_id, {
          id: i.category_parent_id,
          nombre: i.category_parent_name,
          color: i.category_parent_color ?? null,
        });
      }
      const suyas = hijas.get(i.category_parent_id) ?? new Map<string, BotonDeCategoria>();
      if (!suyas.has(i.category_id)) {
        suyas.set(i.category_id, {
          id: i.category_id,
          nombre: i.category_name,
          // Sin color propio, el de su madre: se ve de qué familia es.
          color: i.category_color ?? i.category_parent_color ?? null,
        });
      }
      hijas.set(i.category_parent_id, suyas);
    } else if (!madres.has(i.category_id)) {
      madres.set(i.category_id, {
        id: i.category_id,
        nombre: i.category_name,
        color: i.category_color ?? null,
      });
    }
  }

  return {
    madres: [...madres.values()].sort(porNombre),
    hijasDe: new Map([...hijas].map(([id, m]) => [id, [...m.values()].sort(porNombre)])),
  };
}

/**
 * ¿Este producto se ve con lo elegido? Elegida la madre sin hija, entra todo lo
 * de la familia: lo que está en la madre y lo de cada subcategoría.
 */
export function entraEnLaCategoria(
  i: Pick<ItemListRow, "category_id" | "category_parent_id">,
  madre: string,
  hija: string,
): boolean {
  if (hija) return i.category_id === hija;
  if (madre) return i.category_id === madre || i.category_parent_id === madre;
  return true;
}
